import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Progress,
  Upload,
  type UploadFile,
  type UploadProps,
} from 'antd';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationContext } from '@client/context/NotificationContext';
import { useAuth } from '@client/hooks/useAuth';
import { useRecaptcha } from '@client/hooks/useRecaptcha';
import { TNotificationConfig } from '@client/types';
import { isAllowedMb, isImage, isPdf } from '@client/utils/file.util';
import { logger } from '@client/utils/logger.util';
import {
  cancelUpload,
  uploadRequest,
  type UploadRequestOption,
} from '@client/utils/upload.util';
import { CMaxMb, EFileTypes, EImageTypes } from '@common/types';
import { getEnv } from '@common/utils';

import type { RcFile } from 'antd/es/upload';
import type { CancelTokenSource } from 'axios';

type TUploadFilesProps = {
  listType?: UploadProps['listType'];
  action?: string;
  maxMb?: number;
};

export const UploadFiles: React.FC<TUploadFilesProps> = (props) => {
  const recaptchaKey = getEnv('VITE_RECAPTCHA_SITE_KEY', {
    env: import.meta.env,
    logger,
  });

  const { isRecaptchaLoaded, executeRecaptcha } = useRecaptcha(
    recaptchaKey as string,
  );

  const {
    listType = 'picture-card',
    action = '/upload',
    maxMb = CMaxMb,
  } = props;

  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const { messageApi, notificationApi } = useContext(NotificationContext);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const [percent, setPercent] = useState(0);
  const [fileList, setFileList] = React.useState<UploadFile[]>([]); // manage fileList explicitly
  const [uploading, setUploading] = useState(false);

  const handleChange = async ({
    file,
    fileList: newFileList,
  }: {
    file: UploadFile;
    fileList: UploadFile[];
  }) => {
    setFileList(newFileList);

    // Show upload progress
    if (file.status === 'uploading') {
      const percentage = Math.round(file.percent ?? 0);
      setPercent(percentage);
    } else {
      setPercent(0);
    }
    if (file.status === 'done') {
      messageApi.success(`${file.name} uploaded successfully`);
    } else if (file.status === 'error') {
      messageApi.error(`${file.name} failed to upload`);
    }
  };

  const beforeUpload = (file: RcFile) => {
    const _isPdf = isPdf(file);
    const _isImage = isImage(file);
    const _isLtMax = isAllowedMb(file, maxMb);

    if (!_isPdf && !_isImage) {
      notificationApi.warning({
        title: t('warning'),
        description: t('error.uploadRestriction', {
          extensions: [
            ...Object.keys(EImageTypes),
            ...Object.keys(EFileTypes),
          ].join(', '),
        }),
        ...TNotificationConfig,
      });

      return Upload.LIST_IGNORE;
    }

    if (!_isLtMax) {
      notificationApi.warning({
        title: t('warning'),
        description: t('error.fileSize', { size: maxMb }),
        ...TNotificationConfig,
      });

      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleUpload = async (options: UploadRequestOption) => {
    await uploadRequest({
      ...options,
      setUploading,
      cancelTokenRef,
      executeRecaptcha,
      accessToken,
      action,
      setPercent,
      notificationApi,
      info: {
        defaultRequest: (_option: UploadRequestOption) => {
          // Default Ant Design upload implementation
        },
      },
    });
  };

  const handleCancel = async () => {
    await cancelUpload({
      cancelTokenRef,
      setUploading,
      setPercent,
      messageApi,
    });
  };

  return (
    <div>
      <Upload
        headers={{ Authorization: `Bearer ${accessToken}` }}
        multiple
        disabled={!isRecaptchaLoaded || uploading}
        showUploadList={true}
        fileList={fileList}
        customRequest={handleUpload}
        listType={listType}
        onChange={handleChange}
        beforeUpload={beforeUpload}
      >
        <div>
          <UploadOutlined />
          <div style={{ marginTop: 8 }}>{t('actions.upload')}</div>
        </div>
      </Upload>
      {percent > 0 && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={percent} status="active" />
          <Button
            type="link"
            danger
            onClick={handleCancel}
            style={{ marginTop: 8 }}
          >
            {t('actions.cancelUpload')}
          </Button>
        </div>
      )}
    </div>
  );
};
